import { comparer, observable, reaction, runInAction } from "mobx"
import { addDisposer, getType, IAnyStateTreeNode, Instance, SnapshotIn, types } from "mobx-state-tree"
import { kCaseIdPrefix, kCollectionIdPrefix, typeV3Id, v3Id } from "../../utilities/codap-utils"
import { hashStringSet, hashOrderedStringSet, safeJsonParse } from "../../utilities/js-utils"
import { isValueNonEmpty } from "../../utilities/math-utils"
import { Attribute, IAttribute } from "./attribute"
import {
  CaseInfo, IGroupedCase, IMoveAttributeOptions, symIndex, symParent
} from "./data-set-types"
import { V2Model } from "./v2-model"

// interface to data provided by DataSet
export interface IItemData {
  itemIds: () => string[]
  isHidden: (itemId: string) => boolean
  getValue: (itemId: string, attrId: string) => string
  addItemInfo: (itemId: string, caseId: string) => void
  invalidate: () => void
}

// used for initialization and tests
export const defaultItemData: IItemData = {
  itemIds: () => [],
  isHidden: () => false,
  getValue: () => "",
  addItemInfo: () => null,
  invalidate: () => null
}

// Group keys are used to map from a set of attribute values to a case id (for parent collections)
// and from an item id to a case id (for child collections).
// The format of parent group keys used internally has changed over the course of development.
// Prior to PR #2006, parent group keys were JSON.stringified attribute values.
// In PR #2006, we switched to using square brackets as begin/end markers with tab characters
// as separators, e.g. "[value1\tvalue2\tvalue3]".
// In PR #2157, we switched to using \r characters as begin/end markers to avoid ambiguity.

export const kGroupKeySeparator = "\t"
export const kGroupKeyDelimiter = "\r"

export function makeGroupKey(values: string[]) {
  return `${kGroupKeyDelimiter}${values.join(kGroupKeySeparator)}${kGroupKeyDelimiter}`
}

// functions for pre-processing legacy group keys in snapshots
function preProcessBetaGroupKey(snapGroupKey: string): string {
  const result = /\[(.+)\]/.exec(snapGroupKey)
  if (result?.[1]) return `${kGroupKeyDelimiter}${result[1]}${kGroupKeyDelimiter}`
  // shouldn't ever happen, since we check for [ and ] before calling this function
  /* istanbul ignore next */
  return snapGroupKey
}

function preProcessGroupKey(snapGroupKey: string): string {
  if (snapGroupKey.startsWith(kGroupKeyDelimiter) && snapGroupKey.endsWith(kGroupKeyDelimiter)) return snapGroupKey
  // earlier parent group key formats used [] as begin/end markers
  if (snapGroupKey.startsWith("[") && snapGroupKey.endsWith("]")) {
    // if it contains tab characters, but not \r, then it should be in beta format
    if (snapGroupKey.includes(kGroupKeySeparator)) {
      const result = preProcessBetaGroupKey(snapGroupKey)
      if (result) return result
    }
    // original group key format was JSON.stringified attribute values
    const groupKeyValues = safeJsonParse<string[]>(snapGroupKey)
    if (Array.isArray(groupKeyValues)) return makeGroupKey(groupKeyValues)
    // no tab key and not JSON.parse-able -- single value group key in beta format
    return preProcessBetaGroupKey(snapGroupKey)
  }
  // otherwise, it should be an item id (child group key), so just return it
  return snapGroupKey
}

export const CollectionModel = V2Model
.named("Collection")
.props({
  id: typeV3Id(kCollectionIdPrefix),
  // attributes in left-to-right order
  attributes: types.array(types.safeReference(Attribute)),
  // array of [group key (stringified attribute values), case id] tuples
  // serialized so that case ids are persistent
  _groupKeyCaseIds: types.maybe(types.frozen<Array<[string, string]>>())
})
.volatile(self => ({
  // Init: the following properties are updated by dataSet.syncCollectionLinks
  parent: undefined as ICollectionModel | undefined,
  child: undefined as ICollectionModel | undefined,
  itemData: defaultItemData,

  // Init: groupKeyCaseIds is updated by initializeVolatileState

  // map from group key (stringified attribute values) => case id
  groupKeyCaseIds: new Map<string, string>(),

  // Init: the following case structures are updated by updateCaseGroups.
  // updateCaseGroups is called by dataSet.validateCases
  // if the the dataSet has been invalidated.
  // During initialization the dataset is invalidated by dataSet.syncCollectionLinks.
  // dataSet.validateCases is not called directly by any initialization or update code.
  // dataSet.validateCases is a view that updates volatile state, and validateCases is
  // called by many views of the dataSet. So whenever the cases are actually needed
  // validatedCases should be called and the cases structures will be updated if necessary.
  // NOTE: after an applySnapshot these structures are explicitly cleared, this forces
  // them to be rebuilt and prevents any residual values being left over.

  // all case ids (including hidden cases)
  allCaseIds: new Set<string>(),
  // visible case ids in case table/render order
  caseIds: [] as string[],
  // map from case id to case index
  caseIdToIndexMap: new Map<string, number>(),
  // map from case id to group key (stringified attribute values)
  caseIdToGroupKeyMap: new Map<string, string>(),
  // map from group key (stringified attribute values) to CaseInfo
  caseGroupMap: new Map<string, CaseInfo>(),
  // previous case ids in case table/render order
  prevCaseIds: undefined as Maybe<string[]>,
  // previous map from case id to group key (stringified attribute values)
  prevCaseIdToGroupKeyMap: undefined as Maybe<Map<string, string>>,
  // previous map from group key (stringified attribute values) to CaseInfo
  prevCaseGroupMap: undefined as Maybe<Map<string, CaseInfo>>
}))
.preProcessSnapshot(snap => {
  if (snap._groupKeyCaseIds) {
    const { _groupKeyCaseIds: legacyGroupKeyCaseIds, ...others } = snap
    const _groupKeyCaseIds = legacyGroupKeyCaseIds.map(([snapGroupKey, caseId]) => {
      return [preProcessGroupKey(snapGroupKey), caseId]
    }).filter(([groupKey, caseId]) => !!groupKey && !!caseId) as Array<[string, string]>
    return { _groupKeyCaseIds, ...others }
  }
  return snap
})
.actions(self => ({
  setParent(parent?: ICollectionModel) {
    self.parent = parent
  },
  setChild(child?: ICollectionModel) {
    self.child = child
  },
  setItemData(itemData: IItemData) {
    self.itemData = itemData
  }
}))
.views(self => ({
  getAttribute(attrId: string) {
    return self.attributes.find(attribute => attribute?.id === attrId)
  },
  getAttributeIndex(attrId: string) {
    return self.attributes.findIndex(attribute => attribute?.id === attrId)
  },
  getAttributeByName(name: string) {
    return self.attributes.find(attribute => attribute?.name === name)
  },
  get attributesArray(): IAttribute[] {
    return Array.from(self.attributes).filter(attr => !!attr)
  },
  // non-formula attributes
  get dataAttributesArray(): IAttribute[] {
    return Array.from(self.attributes).filter(attr => attr && !attr.hasFormula) as IAttribute[]
  },
  // sorted non-formula attributes
  get sortedDataAttributes(): IAttribute[] {
    return this.dataAttributesArray.sort((a, b) => a.id.localeCompare(b.id))
  },
  // attributes of all parent collections
  get allParentAttrs(): IAttribute[] {
    const attrs: IAttribute[] = []

    function addParentAttrs(collection: ICollectionModel) {
      if (collection.parent) {
        addParentAttrs(collection.parent)
      }
      collection.attributes.forEach(attr => {
        attr && attrs.push(attr)
      })
    }
    if (self.parent) {
      addParentAttrs(self.parent)
    }

    return attrs
  },
  // non-formula attributes of all parent collections
  get allParentDataAttrs() {
    return this.allParentAttrs.filter(attr => !attr.hasFormula)
  },
  get isTopLevel(): boolean {
    return !self.parent
  }
}))
.views(self => ({
  get allAttributes() {
    return [...self.allParentAttrs, ...self.attributesArray]
  },
  // all non-formula
  get allDataAttributes() {
    // sort the attributes by id so that original attribute order doesn't affect the result
    return [...self.allParentDataAttrs, ...self.dataAttributesArray].sort((a, b) => a.id.localeCompare(b.id))
  },
  // sorted non-formula attributes of parent collections
  get sortedParentDataAttrs() {
    return self.allParentDataAttrs.sort((a, b) => a.id.localeCompare(b.id))
  }
}))
.views(self => ({
  groupKey(itemId: string) {
    // only parent collections group cases; child collections "group" by itemId
    if (!self.child) return itemId
    return makeGroupKey(self.allDataAttributes.map(attr => self.itemData.getValue(itemId, attr.id)))
  },
  parentGroupKey(itemId: string) {
    if (!self.parent) return
    return makeGroupKey(self.sortedParentDataAttrs.map(attr => self.itemData.getValue(itemId, attr.id)))
  },
  groupKeyCaseId(groupKey?: string) {
    if (!groupKey) return undefined
    let caseId = self.groupKeyCaseIds.get(groupKey)
    if (!caseId) {
      caseId = v3Id(kCaseIdPrefix)
      self.groupKeyCaseIds.set(groupKey, caseId)
    }
    return caseId
  }
}))
.views(self => ({
  getChildItemsToken(caseId: string) {
    const groupKey = self.caseIdToGroupKeyMap.get(caseId)
    const caseGroup = groupKey ? self.caseGroupMap.get(groupKey) : undefined
    const childItemIds = caseGroup?.childItemIds.length ? caseGroup.childItemIds : undefined
    return childItemIds?.sort().join()
  },
  getPrevChildItemsToken(caseId: string) {
    const groupKey = self.prevCaseIdToGroupKeyMap?.get(caseId)
    const caseGroup = groupKey ? self.prevCaseGroupMap?.get(groupKey) : undefined
    const childItemIds = caseGroup?.childItemIds.length ? caseGroup.childItemIds : undefined
    return childItemIds?.sort().join()
  }
}))
.actions(self => ({
  clearCases() {
    self.allCaseIds.clear()
    if (!self.prevCaseIds) self.prevCaseIds = self.caseIds
    self.caseIds = []

    self.caseIdToIndexMap.clear()

    if (!self.prevCaseIdToGroupKeyMap) self.prevCaseIdToGroupKeyMap = self.caseIdToGroupKeyMap
    self.caseIdToGroupKeyMap = new Map<string, string>()

    if (!self.prevCaseGroupMap) self.prevCaseGroupMap = self.caseGroupMap
    self.caseGroupMap = new Map<string, CaseInfo>()
  },
  clearPrevCases() {
    self.prevCaseIds = undefined
    self.prevCaseIdToGroupKeyMap = undefined
    self.prevCaseGroupMap = undefined
  }
}))
.views(self => ({
  // returns a map from newly assigned case id to previously used case id
  getRemappedCaseIds(newCaseIds: string[]) {
    // See if any case ids should be remapped. This occurs when the grouping values of a parent
    // case change in unison, in which case we preserve the original case id rather than assigning
    // a new one. Because the grouping values are used to generate the groupKey, and groupKeys are
    // mapped to case ids, normally changing grouping values results in generation of a new id.
    // To detect this, we identify case ids that were in use the last time we grouped cases as
    // well as newly generated case ids corresponding to new groupKeys. If there are any recently
    // unused case ids that correspond to the same set of items as any of the newly generated case
    // ids, then we replace the new case id with the original case id in our internal structures.
    // We have to do this in a second pass because we don't know the full set of child item ids
    // associated with a particular case id or groupKey until the completion of the first pass.
    // This assumes that from one grouping pass to the next, either items were added/removed
    // (in which case sets of child item ids may have changed but case ids should be persistent)
    // OR item values were changed (in which case case ids may have changed but sets of child item
    // ids will not have changed). If both sets of changes occur in one pass then the remapping
    // algorithm won't recognize the new cases as appropriate to inherit the previous case ids.
    const remappedCaseIds = new Map<string, string>() // new case id => original case id
    if (self.prevCaseIds) {
      // identify recently released case ids no longer in use
      const unusedCaseIds = self.prevCaseIds.filter(caseId => !self.caseIdToGroupKeyMap.get(caseId))
      if (unusedCaseIds.length && newCaseIds.length) {
        // determine the set of child item ids corresponding to each unused case id
        const unusedChildItemTokens = new Map<string, string>()
        unusedCaseIds.forEach(caseId => {
          const childItemToken = self.getPrevChildItemsToken(caseId)
          if (childItemToken) {
            unusedChildItemTokens.set(childItemToken, caseId)
          }
        })
        // see if any newly assigned case ids correspond to sets of items previously
        // associated with one of the recently released case ids no longer in use
        newCaseIds.forEach(newCaseId => {
          const childItemToken = self.getChildItemsToken(newCaseId)
          const unusedCaseIdForToken = childItemToken && unusedChildItemTokens.get(childItemToken)
          if (unusedCaseIdForToken) {
            // found an unused case id corresponding to the same child items as a new case id
            remappedCaseIds.set(newCaseId, unusedCaseIdForToken)
          }
        })
      }
    }
    return remappedCaseIds
  }
}))
.views(self => ({
  // Passing itemIds regroups just those items instead of starting from scratch, which is how
  // appended items are handled; omitting them rebuilds the collection from all of its items.
  // Insertions take the rebuild path.
  //
  // Note that appending items does not imply appending cases: a child collection orders its
  // cases by parent case and then by position within the parent, so items added at the end of
  // the item list still belong in the middle of that order whenever they join an older parent.
  // completeCaseGroups works that out from the parents' case indices; no caller of this method
  // is in a position to tell it in advance.
  updateCaseGroups(itemIds?: string[]) {
    // newCaseIds semantics differ between rebuild and additive paths — see the push site.
    const isFullRebuild = !itemIds
    if (!itemIds) {
      self.clearCases()
      itemIds = self.itemData.itemIds()
    }

    const newCaseIds: string[] = []
    // cases that already existed but became visible during this pass
    const unhiddenCaseIds: string[] = []
    // key is child caseId
    const parentChildIdMap = new Map<string, { parentCaseId: string, isHidden: boolean }>()
    const itemInfo: Array<{itemId: string, caseId: string}> = []
    itemIds.forEach(itemId => {
      const isItemHidden = self.itemData.isHidden(itemId)
      const groupKey = self.groupKey(itemId)
      const hadCaseIdForGroupKey = !!self.groupKeyCaseIds.get(groupKey)
      const caseId = self.groupKeyCaseId(groupKey)
      if (groupKey && caseId) {
        let caseGroup = self.caseGroupMap.get(groupKey)
        if (!caseGroup) {
          // newCaseIds feeds two consumers: completeCaseGroups' append branch needs every
          // newly-created caseGroupMap entry (including ones whose case ID was preserved
          // across a prior wipe of caseGroupMap), while getRemappedCaseIds in the full-
          // rebuild path must not see preserved-id cases or it'd treat them as candidates
          // for remapping.
          if (!isFullRebuild || !hadCaseIdForGroupKey) {
            newCaseIds.push(caseId)
          }
          self.allCaseIds.add(caseId)
          // cases with only hidden items aren't in caseIds and don't get indices
          const newCaseIndex = isItemHidden ? -1 : self.caseIds.length
          !isItemHidden && self.caseIds.push(caseId)
          self.caseIdToIndexMap.set(caseId, newCaseIndex)
          self.caseIdToGroupKeyMap.set(caseId, groupKey)

          const parentGroupKey = self.parentGroupKey(itemId)
          const parentCaseId = self.parent?.groupKeyCaseId(parentGroupKey)
          const parent = parentCaseId ? { [symParent]: parentCaseId } : {}
          // stash parent/child pairs so they can be remapped if necessary
          parentCaseId && parentChildIdMap.set(caseId, { parentCaseId, isHidden: isItemHidden })

          caseGroup = {
            collectionId: self.id,
            groupedCase: {
              __id__: caseId,
              ...parent,
              [symIndex]: newCaseIndex
            },
            childItemIds: isItemHidden ? [] : [itemId],
            hiddenChildItemIds: isItemHidden ? [itemId] : [],
            groupKey,
            // case is hidden if all of its items are hidden
            isHidden: isItemHidden
          }
          self.caseGroupMap.set(groupKey, caseGroup)
        }
        // case group already exists
        else {
          // If case is hidden, then this is its first visible item. Need to make the case visible.
          if (!isItemHidden && caseGroup.isHidden) {
            const newCaseIndex = self.caseIds.length
            const parentChildInfo = parentChildIdMap.get(caseId)
            self.caseIds.push(caseId)
            self.caseIdToIndexMap.set(caseId, newCaseIndex)
            if (parentChildInfo) {
              parentChildInfo.isHidden = false
            }
            else {
              // The case was grouped in an earlier pass, so it has no entry here and its parent
              // has never been told about it — it was hidden the last time the parent collected
              // its children. Where it belongs among its siblings follows from item order,
              // which only a regroup from scratch can work out, so report it and let the caller
              // start over rather than appending it to the end of its parent's children.
              unhiddenCaseIds.push(caseId)
            }
            caseGroup.groupedCase[symIndex] = newCaseIndex
            caseGroup.isHidden = false
          }
          if (isItemHidden) {
            caseGroup.hiddenChildItemIds.push(itemId)
          }
          else {
            caseGroup.childItemIds.push(itemId)
          }
        }

        // item info is stored for all items
        itemInfo.push({ itemId, caseId })
      }
    })

    // Identify any new case ids that should be replaced with a prior case id
    const remappedCaseIds = self.getRemappedCaseIds(newCaseIds)

    // add item info, remapping case ids where appropriate
    itemInfo.forEach(({ itemId, caseId }) => {
      const _caseId = remappedCaseIds.get(caseId) ?? caseId
      self.itemData.addItemInfo(itemId, _caseId)
    })

    // add child case ids to parent cases, remapping child case ids where appropriate
    parentChildIdMap.forEach(({ parentCaseId, isHidden }, _childCaseId) => {
      if (!isHidden) {
        const childCaseId = remappedCaseIds.get(_childCaseId) ?? _childCaseId
        self.parent?.addChildCase(parentCaseId, childCaseId)
      }
    })

    // remap case ids in our internal structures
    if (remappedCaseIds.size) {
      self.caseIds.forEach((caseId, i) => {
        const remappedCaseId = remappedCaseIds.get(caseId)
        if (remappedCaseId) {
          self.caseIds[i] = remappedCaseId
        }
      })
    }
    Array.from(remappedCaseIds.entries()).forEach(([newCaseId, origCaseId]) => {
      // update index map
      const caseIndex = self.caseIdToIndexMap.get(newCaseId)
      if (caseIndex != null) {
        self.caseIdToIndexMap.delete(newCaseId)
        self.caseIdToIndexMap.set(origCaseId, caseIndex)
      }
      // update group key-case id relationships
      const groupKey = self.caseIdToGroupKeyMap.get(newCaseId)
      if (groupKey != null) {
        // update group key to case id map
        const origGroupKey = self.prevCaseIdToGroupKeyMap?.get(origCaseId)
        origGroupKey && self.groupKeyCaseIds.delete(origGroupKey)
        self.groupKeyCaseIds.set(groupKey, origCaseId)

        // update case id to group key map
        self.caseIdToGroupKeyMap.delete(newCaseId)
        self.caseIdToGroupKeyMap.set(origCaseId, groupKey)

        // update case group map entry
        const caseGroup = self.caseGroupMap.get(groupKey)
        if (caseGroup) {
          caseGroup.groupedCase.__id__ = origCaseId
        }
      }
    })
    self.clearPrevCases()

    return { newCaseIds, unhiddenCaseIds }
  }
}))
.views(self => ({
  hasCase(caseId: string) {
    return self.caseIdToIndexMap.has(caseId)
  },
  getCaseIndex(caseId: string) {
    return self.caseIdToIndexMap.get(caseId)
  },
  getCaseGroup(caseId: string) {
    const groupKey = self.caseIdToGroupKeyMap.get(caseId)
    return groupKey ? self.caseGroupMap.get(groupKey) : undefined
  },
  isNonEmptyCaseGroup(caseGroup: CaseInfo) {
    // parent cases are always considered non-empty
    if (self.child) return true
    // cases with no child items are considered empty
    if (!caseGroup?.childItemIds.length) return false
    // cases with multiple child items are non-empty (shouldn't happen for child collections)
    if (caseGroup.childItemIds.length > 1) return true
    // For child collections, determine non-empty status by checking child item values across
    // all attributes — including formula-driven ones. Formula-evaluated values count as
    // non-empty by definition (a case with a computed value is not empty), so we check
    // attributesArray rather than dataAttributesArray (which excludes formula attrs).
    const childItemId = caseGroup.childItemIds[0]
    return self.attributesArray.some(attr => isValueNonEmpty(self.itemData.getValue(childItemId, attr.id)))
  },
  addChildCase(parentCaseId: string, childCaseId: string) {
    const groupKey = self.caseIdToGroupKeyMap.get(parentCaseId)
    const caseGroup = groupKey && self.caseGroupMap.get(groupKey)
    if (caseGroup) {
      if (!caseGroup.childCaseIds) {
        caseGroup.childCaseIds = [childCaseId]
      }
      else {
        caseGroup.childCaseIds.push(childCaseId)
      }
    }
    else {
      console.warn("CollectionModel.addChildCase -- missing parent case:", parentCaseId)
    }
  },
}))
.extend(self => {
  // Plain cached values (NOT observable — no MobX involvement)
  let _caseGroups: CaseInfo[] = []
  let _cases: IGroupedCase[] = []
  let _nonEmptyCases: IGroupedCase[] = []
  let _caseIdsHash = 0
  let _caseIdsOrderedHash = 0

  // Two observable version counters track when cached views need re-evaluation:
  //   _groupingChangeVersion — bumped by completeCaseGroups() after a full rebuild. All
  //     grouping-affected views invalidate together (caseGroups, cases, nonEmptyCases, hashes).
  //   _valueChangeVersion — bumped by recomputeNonEmptyCases() when child-collection
  //     attribute values change without affecting groupings (e.g. formula recomputation).
  //     Only views derived from values read this; other views ignore it.
  // Invariant: a grouping change subsumes a value change — the rebuild path refreshes
  // _nonEmptyCases as part of completeCaseGroups, so it bumps only _groupingChangeVersion.
  // Note: writing to these observables from a view via runInAction is a pragmatic compromise,
  // but it avoids the circular read/write pattern of the old code (multiple observable boxes
  // read then written in the same view) and ensures reactions never see stale cached data.
  const _groupingChangeVersion = observable.box(0)
  const _valueChangeVersion = observable.box(0)

  // Invalidation state (plain, not observable)
  let _needsFullRebuild = true   // starts true: no valid data yet
  let _pendingNewCaseIds: string[] | undefined

  // Cases in a child collection are ordered by parent case, then by position within the
  // parent. New cases are always pushed onto the end of their parent's childCaseIds, so an
  // append extends the existing order — rather than inserting into the middle of it — only
  // when each new case's parent is at or after the parent of the case that currently sits
  // last. Streamed appends (a plugin adding samples to the newest experiment) satisfy this;
  // adding to an older parent doesn't, and has to fall back to re-sorting the collection.
  const appendExtendsOrder = (groups: CaseInfo[]) => {
    const parentIndexOf = (group: CaseInfo) => {
      const parentCaseId = group.groupedCase[symParent]
      return parentCaseId != null ? self.parent?.getCaseIndex(parentCaseId) : undefined
    }
    // Where the existing cases end. If the last one's parent can't be located there is no
    // baseline to compare against, so fall back to re-sorting rather than assume the append
    // belongs at the end.
    let parentIndex = -1
    const lastCompleted = _caseGroups[_caseGroups.length - 1]
    if (lastCompleted) {
      const lastParentIndex = parentIndexOf(lastCompleted)
      if (lastParentIndex == null) return false
      parentIndex = lastParentIndex
    }
    return groups.every(group => {
      const groupParentIndex = parentIndexOf(group)
      if (groupParentIndex == null || groupParentIndex < parentIndex) return false
      parentIndex = groupParentIndex
      return true
    })
  }

  return {
    views: {
      get caseGroups() {
        _groupingChangeVersion.get()
        return _caseGroups
      },
      get cases() {
        _groupingChangeVersion.get()
        return _cases
      },
      get nonEmptyCases() {
        _groupingChangeVersion.get()
        _valueChangeVersion.get()
        return _nonEmptyCases
      },
      get caseIdsHash() {
        _groupingChangeVersion.get()
        return _caseIdsHash
      },
      get caseIdsOrderedHash() {
        _groupingChangeVersion.get()
        return _caseIdsOrderedHash
      },
      completeCaseGroups(parentCases: Maybe<CaseInfo[]>) {
        const newCaseIds = _pendingNewCaseIds
        _pendingNewCaseIds = undefined

        const newGroups = !_needsFullRebuild && newCaseIds?.length
          ? newCaseIds
              .map(caseId => self.getCaseGroup(caseId))
              .filter((group): group is CaseInfo => !!group)
          : undefined

        // A collection that had no case added to it is left exactly as it was — same cases,
        // same order, same indices, with only the contents of existing case groups mutated in
        // place, which the version bump at the end reports. A parent collection can reuse its
        // caches on that basis, since its nonEmptyCases simply mirror its cases. The childmost
        // collection cannot: emptiness there is derived from item values, and re-sending an
        // item id writes new values into a case that already exists.
        const mustRecompute =
          _needsFullRebuild || newCaseIds == null || newCaseIds.length > 0 || !self.child

        // Hidden-only groups are excluded so this matches REBUILD's walk of self.caseIds
        // (which already excludes them), which means the result can be empty even when
        // newCaseIds isn't (e.g. when re-adding previously deleted items whose group key
        // mappings still exist).
        const newCaseGroups = newGroups?.filter(group => !group.isHidden)

        // The groups to complete additively, or undefined to rebuild the caches from scratch.
        // A child collection appends only when its own case list grew and the new cases sort
        // to the end; a top-level collection completes additively regardless, since an append
        // that contributed no visible cases leaves its existing completion correct as-is.
        // A child collection also rebuilds when any of the new cases is hidden: its REBUILD
        // repopulates caseIdToIndexMap from caseIds and so drops the placeholder index a
        // hidden case is given, and appending has no equivalent step.
        const appendedCaseGroups = parentCases
          ? newCaseGroups?.length && newCaseGroups.length === newGroups?.length &&
              appendExtendsOrder(newCaseGroups)
              ? newCaseGroups
              : undefined
          : newCaseGroups

        if (parentCases && mustRecompute) {
          if (appendedCaseGroups) {
            // The appended cases occupy the tail of their parent's childCaseIds, so their
            // index within the parent counts back from the end of that list. Every other case
            // in the collection keeps the position it already had.
            const groupsByParent = new Map<string, CaseInfo[]>()
            appendedCaseGroups.forEach(group => {
              const parentCaseId = group.groupedCase[symParent]
              if (parentCaseId == null) return
              const groups = groupsByParent.get(parentCaseId)
              if (groups) groups.push(group)
              else groupsByParent.set(parentCaseId, [group])
            })
            groupsByParent.forEach((groups, parentCaseId) => {
              const childCaseIds = self.parent?.getCaseGroup(parentCaseId)?.childCaseIds
              // Without the parent's children — or with fewer of them than we are placing —
              // there is no way to say where these cases sit among their siblings, and counting
              // back would give them negative indices. Leave the indices updateCaseGroups
              // assigned and report it, as addChildCase already does for the same condition.
              if (!childCaseIds || childCaseIds.length < groups.length) {
                console.warn("CollectionModel.completeCaseGroups -- missing parent case:", parentCaseId)
                return
              }
              const firstNewIndex = childCaseIds.length - groups.length
              groups.forEach((group, index) => {
                group.groupedCase[symIndex] = firstNewIndex + index
              })
            })
          }
          else {
            self.caseIds.splice(0, self.caseIds.length)
            // sort cases by parent cases
            parentCases.forEach(parentCase => {
              const childCaseIds = parentCase.childCaseIds ?? []
              // update indices
              childCaseIds.forEach((childCaseId, index) => {
                const caseGroup = self.getCaseGroup(childCaseId)
                caseGroup && (caseGroup.groupedCase[symIndex] = index)
              })
              // append case ids in grouped order
              self.caseIds.push(...childCaseIds)
            })
            // rebuild the case id to index map, since the order of child cases may have changed
            self.caseIdToIndexMap.clear()
            self.caseIds.forEach((caseId, index) => {
              self.caseIdToIndexMap.set(caseId, index)
            })
          }
        }

        // APPEND path: only new cases were added
        // Uses concatenation (not mutation) so consumers holding old references see a new array.
        if (appendedCaseGroups) {
          _caseGroups = [..._caseGroups, ...appendedCaseGroups]

          const newCases = appendedCaseGroups.map(group => group.groupedCase)
          _cases = [..._cases, ...newCases]

          // Parent collections: every case is non-empty by definition (isNonEmptyCaseGroup
          // short-circuits to true), so reuse newCases rather than running the filter.
          const newNonEmptyCases = self.child
            ? newCases
            : appendedCaseGroups
                .filter(group => self.isNonEmptyCaseGroup(group))
                .map(group => group.groupedCase)
          _nonEmptyCases = [..._nonEmptyCases, ...newNonEmptyCases]
        }
        // REBUILD path: full recompute from volatile state
        else if (mustRecompute) {
          _caseGroups = self.caseIds
                          .map(caseId => self.getCaseGroup(caseId))
                          .filter(group => !!group)

          _cases = _caseGroups
                      .map(group => group?.groupedCase)
                      .filter(groupedCase => !!groupedCase)

          // Parent collections: every case is non-empty by definition (isNonEmptyCaseGroup
          // short-circuits to true), so _nonEmptyCases content equals _cases. A shallow copy
          // preserves reference-identity semantics (each rebuild produces a fresh array, so
          // identity-comparing reactions on nonEmptyCases still fire when caseGroups change).
          _nonEmptyCases = self.child
            ? [..._cases]
            : _caseGroups
                .map(group => group && self.isNonEmptyCaseGroup(group) ? group.groupedCase : undefined)
                .filter(aCase => !!aCase)

          _needsFullRebuild = false
        }
        // UNCHANGED: no case was added here, so the cached contents still hold. The case
        // groups themselves may have been mutated in place, though — a descendant collection
        // adds to their childCaseIds — so hand out fresh arrays, because the rebuild path
        // treats a new array identity as the signal that something changed and consumers
        // identity-compare on that basis.
        else {
          _caseGroups = [..._caseGroups]
          _cases = [..._cases]
          _nonEmptyCases = [..._nonEmptyCases]
        }

        _caseIdsHash = hashStringSet(self.caseIds)
        _caseIdsOrderedHash = hashOrderedStringSet(self.caseIds)

        // Signal to MobX that cached data has been updated.
        // Must happen AFTER plain variables are updated so reactions see fresh data.
        runInAction(() => _groupingChangeVersion.set(_groupingChangeVersion.get() + 1))
      }
    },
    actions: {
      // Full invalidation — signals complete rebuild needed.
      // Called from DataSet.invalidateCases() (always in action context).
      invalidateCaseGroups() {
        _needsFullRebuild = true
        _pendingNewCaseIds = undefined
      },
      // Additive invalidation — signals only new cases were added.
      // Called from DataSet.validateCasesForNewItems() (action context).
      // Only enables append path if no full rebuild is pending.
      invalidateCaseGroupsForNewCases(newCaseIds: string[]) {
        if (!_needsFullRebuild) {
          _pendingNewCaseIds = newCaseIds
        }
      },
      // Re-derive _nonEmptyCases from the existing _caseGroups without redoing any grouping.
      // Used after child-collection attribute values change (e.g. formula recomputation), where
      // emptiness can flip but groupings cannot. Skipped if a full rebuild is already pending —
      // completeCaseGroups will refresh _nonEmptyCases anyway, and walking stale _caseGroups
      // would produce a transiently wrong result. Also a no-op on parent collections, where
      // every case is non-empty by definition (isNonEmptyCaseGroup short-circuits to true), so
      // a value-only change can never flip emptiness — DataSet.validateCases already filters to
      // the childmost collection, but this guard documents the action's contract.
      recomputeNonEmptyCases() {
        if (_needsFullRebuild || self.child) return
        _nonEmptyCases = _caseGroups
          .filter(group => self.isNonEmptyCaseGroup(group))
          .map(group => group.groupedCase)
        _valueChangeVersion.set(_valueChangeVersion.get() + 1)
      }
    }
  }
})
.actions(self => ({
  initializeVolatileState() {
    if (self._groupKeyCaseIds) {
      self.groupKeyCaseIds = new Map<string, string>(self._groupKeyCaseIds)
    }
  }
}))
.actions(self => ({
  afterCreate() {
    self.initializeVolatileState()

    // changes to a parent collection's attributes invalidate grouping
    addDisposer(self, reaction(
      () => self.sortedDataAttributes.map(attr => attr.id),
      () => {
        if (self.child) {
          // There's a tradeoff here. By preserving the group key => case id mappings across
          // hierarchy changes, we allow case ids to be persistent across such hierarchy changes.
          // For instance, removing an attribute from a collection (which causes re-grouping) and
          // then adding it back will result in the same case ids as before. The cost of this,
          // however, is that collections maintain a history of all of the group key => case id
          // mappings that have ever come before, and this gets serialized as well. Conversely,
          // we can reduce the memory used by the collection and the size of the serialized
          // document by clearing the map on hierarchy changes and accepting that new case ids
          // will be generated at these times. At this writing, it seems unlikely that large
          // documents with large numbers of cases that go through lots of different parent
          // case groupings will be particularly common, but we can revisit if necessary.
          // self.groupKeyCaseIds.clear()
          self.itemData.invalidate()
        }
      }, { name: "CollectionModel.sortedDataAttributes reaction", equals: comparer.structural }
    ))
  },
  prepareSnapshot() {
    // Only the mappings for the current groupings are serialized. The volatile map is
    // deliberately _not_ pruned here -- retaining the full history of mappings is what allows
    // case ids to survive hierarchy changes within a session (see the reaction in afterCreate).
    // Serializing that history, however, can dominate the size of a saved document for datasets
    // that churn through large numbers of items (e.g. the Sampler plugin, which can accumulate
    // tens of thousands of dead mappings in a document with no remaining data). The cost of
    // pruning the serialized copy is that a hierarchy change which straddles a save/load will
    // generate new case ids rather than restoring the previous ones.
    // caseGroupMap is keyed by group key and is up to date whenever this runs, because
    // DataSet.prepareSnapshot() calls validateCases() before delegating to its collections, and
    // validateCases() regroups every collection, not just the childmost one. Hidden/set-aside
    // cases are grouped like any other (with isHidden set), so their mappings are retained.
    // We iterate the (live) caseGroupMap rather than filtering the (historical) groupKeyCaseIds
    // so that the work is proportional to the number of current groups rather than to the full
    // history. The case id comes from the case group rather than from groupKeyCaseIds because
    // the group's own id is what a reloaded document needs to reproduce; the two are always in
    // sync (groupKeyCaseIds is only written where the corresponding groupedCase.__id__ is set).
    const groupKeyCaseIds: Array<[string, string]> = []
    self.caseGroupMap.forEach((caseGroup, groupKey) => {
      groupKeyCaseIds.push([groupKey, caseGroup.groupedCase.__id__])
    })
    self._groupKeyCaseIds = groupKeyCaseIds
  },
  completeSnapshot() {
  },
  afterApplySnapshot() {
    // To be safe we clear out volatile state that might not be valid anymore.
    // Things do seem to work without this, but there is residual state in
    // some of the case structures, and the previous case structures can end
    // up pointing at the old data

    // The following volatile properties get updated by dataSet.syncCollectionLinks
    self.parent = undefined
    self.child = undefined
    self.itemData = defaultItemData

    self.clearCases()
    // clearPrevCases has to be called after clearCases because clearCases
    // sets prevCaseIds, prevCaseIdToGroupKeyMap, and prevCaseGroupMap
    self.clearPrevCases()

    self.initializeVolatileState()
  },
  addAttribute(attr: IAttribute, options?: IMoveAttributeOptions) {
    const beforeIndex = options?.before ? self.getAttributeIndex(options.before) : -1
    const afterIndex = options?.after ? self.getAttributeIndex(options.after) : -1
    if (beforeIndex >= 0) {
      self.attributes.splice(beforeIndex, 0, attr)
    }
    else if (afterIndex >= 0) {
      self.attributes.splice(afterIndex + 1, 0, attr)
    }
    else {
      self.attributes.push(attr)
    }
  },
  removeAttribute(attrId: string) {
    const attr = self.getAttribute(attrId)
    attr && self.attributes.remove(attr)
  }
}))
.actions(self => ({
  moveAttribute(attrId: string, options?: IMoveAttributeOptions) {
    const attr = self.getAttribute(attrId)
    // is the attribute being moved before/after itself?
    const isMoving = attrId !== options?.after && attrId !== options?.before
    if (attr && isMoving) {
      self.removeAttribute(attr.id)
      self.addAttribute(attr, options)
    }
  }
}))
export interface ICollectionModel extends Instance<typeof CollectionModel> {}
export interface ICollectionModelSnapshot extends SnapshotIn<typeof CollectionModel> {}

export function isCollectionModel(model?: IAnyStateTreeNode): model is ICollectionModel {
  return !!model && getType(model) === CollectionModel
}

export function syncCollectionLinks(collections: ICollectionModel[], itemData: IItemData) {
  collections.forEach((collection, index) => {
    if (index === 0) {
      collection.setParent()
    }
    if (index > 0) {
      collection.setParent(collections[index - 1])
    }
    if (index < collections.length - 1) {
      collection.setChild(collections[index + 1])
    }
    if (index === collections.length - 1) {
      collection.setChild()
    }
    collection.setItemData(itemData)
  })
}
